import { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SceneEntry from '@/components/scenes/SceneEntry';
import SceneProblem from '@/components/scenes/SceneProblem';
import SceneFormula from '@/components/scenes/SceneFormula';
import SceneSelectedWork from '@/components/scenes/SceneSelectedWork';
import SceneIdentityGate from '@/components/scenes/SceneIdentityGate';
import ScenePackages from '@/components/scenes/ScenePackages';
import SceneCustomProject from '@/components/scenes/SceneCustomProject';
import SceneProcess from '@/components/scenes/SceneProcess';
import SceneExit from '@/components/scenes/SceneExit';
import UpgradesModal from '@/components/UpgradesModal';
import CustomProjectModal from '@/components/CustomProjectModal';

gsap.registerPlugin(ScrollTrigger);

const FrmlaExperience = () => {
  const [selectedIdentity, setSelectedIdentity] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedUpgrades, setSelectedUpgrades] = useState([]);
  const [upgradesModalOpen, setUpgradesModalOpen] = useState(false);
  const [customProjectModalOpen, setCustomProjectModalOpen] = useState(false);
  const [pendingPackage, setPendingPackage] = useState(null);
  const containerRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });
  
  // Progress bar
  const progressWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  
  useEffect(() => {
    // Initialize GSAP ScrollTrigger for scenes
    const scenes = gsap.utils.toArray('.scene');
    
    scenes.forEach((scene, i) => {
      ScrollTrigger.create({
        trigger: scene,
        start: "top center",
        end: "bottom center",
        onEnter: () => {
          scene.classList.add('active');
        },
        onLeaveBack: () => {
          scene.classList.remove('active');
        }
      });
    });
    
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);
  
  const handleIdentitySelect = (identity) => {
    setSelectedIdentity(identity);
    setSelectedPackage(null);
    setSelectedUpgrades([]);
    // Scroll to packages section
    setTimeout(() => {
      document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };
  
  const handlePackageSelect = (pkg) => {
    // Call-only packages go directly to calendar booking
    if (pkg.callOnly) {
      window.open('https://calendly.com', '_blank');
      return;
    }
    
    // Purchasable packages show the upgrades modal
    setPendingPackage(pkg);
    setUpgradesModalOpen(true);
  };
  
  const handleUpgradesContinue = (upgrades) => {
    setSelectedUpgrades(upgrades);
    setSelectedPackage(pendingPackage);
    setUpgradesModalOpen(false);
    // TODO: Redirect to Stripe with upgrades
    // For now, scroll to checkout section
    setTimeout(() => {
      document.getElementById('checkout')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };
  
  const handleUpgradesSkip = () => {
    setSelectedUpgrades([]);
    setSelectedPackage(pendingPackage);
    setUpgradesModalOpen(false);
    // TODO: Redirect to base Stripe link
    // For now, scroll to checkout section
    setTimeout(() => {
      document.getElementById('checkout')?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };
  
  const handleCheckout = () => {
    // TODO: Redirect to Stripe checkout
    alert('Redirecting to Stripe checkout...');
  };
  
  const calculateTotal = () => {
    return selectedPackage?.price || 0;
  };

  return (
    <div ref={containerRef} className="relative bg-background">
      {/* Progress bar */}
      <motion.div 
        className="fixed top-0 left-0 h-[2px] bg-accent z-50"
        style={{ width: progressWidth }}
      />
      
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Cross pattern overlay */}
      <div className="fixed inset-0 cross-pattern opacity-30 pointer-events-none z-0" />
      
      {/* SCENE 01: THE ENTRY */}
      <SceneEntry />
      
      {/* SCENE 02: THE PROBLEM */}
      <SceneProblem />
      
      {/* SCENE 03: THE FORMULA */}
      <SceneFormula />
      
      {/* SCENE 04: IDENTITY GATE */}
      <SceneIdentityGate 
        selectedIdentity={selectedIdentity}
        onSelect={handleIdentitySelect}
      />
      
      {/* SCENE 05: PACKAGES */}
      <ScenePackages 
        selectedIdentity={selectedIdentity}
        selectedPackage={selectedPackage}
        onSelectPackage={handlePackageSelect}
      />
      
      {/* SCENE 06: CUSTOM PROJECT */}
      <SceneCustomProject 
        onOpenForm={() => setCustomProjectModalOpen(true)}
      />
      
      {/* SCENE 07: THE PROCESS */}
      <SceneProcess />
      
      {/* SCENE 08: PROOF */}
      <SceneSelectedWork />
      
      {/* SCENE 09: EXIT / CTA */}
      <SceneExit 
        selectedPackage={selectedPackage}
        selectedUpgrades={selectedUpgrades}
        total={calculateTotal()}
        onCheckout={handleCheckout}
      />
      
      {/* Upgrades Modal - appears when clicking "Start This Package" */}
      <AnimatePresence>
        {upgradesModalOpen && (
          <UpgradesModal 
            isOpen={upgradesModalOpen}
            onClose={() => setUpgradesModalOpen(false)}
            selectedPackage={pendingPackage}
            selectedIdentity={selectedIdentity}
            onContinue={handleUpgradesContinue}
            onSkip={handleUpgradesSkip}
          />
        )}
      </AnimatePresence>
      
      {/* Custom Project Modal - appears when clicking "Get a Game Plan" */}
      <AnimatePresence>
        {customProjectModalOpen && (
          <CustomProjectModal 
            isOpen={customProjectModalOpen}
            onClose={() => setCustomProjectModalOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default FrmlaExperience;
